import { useCallback, useEffect, useState } from "react";
import { Search, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const Th = ({ children }) => <th className="text-left text-[10px] uppercase tracking-label text-gray-500 font-semibold px-2 py-1.5">{children}</th>;
const Td = ({ children, mono }) => <td className={`px-2 py-1.5 text-xs text-gray-700 ${mono ? "font-mono" : ""}`}>{children ?? "-"}</td>;

function RowsTable({ rows, testId }) {
  if (!rows.length) return <p className="text-xs text-gray-400 italic">None</p>;
  return (
    <div className="overflow-x-auto border border-gray-100 rounded-md">
      <table className="w-full" data-testid={testId}>
        <thead className="bg-gray-50"><tr><Th>FM Record</Th><Th>Size</Th><Th>Contract</Th><Th>Invoice</Th><Th>Qty</Th><Th>CI Qty</Th><Th>In Quote</Th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.entry_id || r.fm_record_id} className="border-t border-gray-100">
              <Td mono>{r.fm_record_id || "-"}</Td><Td>{r.size}</Td><Td mono>{r.contract_no}</Td><Td mono>{r.invoice_no}</Td><Td>{r.qty}</Td><Td>{r.ci_inventory_qty}</Td>
              <Td>{r.in_cart === undefined ? "" : r.in_cart ? "Yes" : "No"}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-gray-100 rounded-md px-3 py-2 bg-gray-50/60">
      <p className="text-[10px] uppercase tracking-label text-gray-500">{label}</p>
      <p className={`text-lg font-semibold font-mono ${accent || "text-gray-900"}`}>{value ?? "-"}</p>
    </div>
  );
}

export default function FileMakerAuditPanel() {
  const [itemNo, setItemNo] = useState("");
  const [itemLoading, setItemLoading] = useState(false);
  const [itemResult, setItemResult] = useState(null);
  const [full, setFull] = useState({ running: false });

  const loadStatus = useCallback(async () => {
    try { const { data } = await api.get("/filemaker/audit/full/status"); setFull(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    if (!full.running) return undefined;
    const id = setInterval(loadStatus, 3000);
    return () => clearInterval(id);
  }, [full.running, loadStatus]);

  const compareItem = async () => {
    const s = itemNo.trim();
    if (!s) return;
    setItemLoading(true);
    try {
      const { data } = await api.get(`/filemaker/audit/item/${encodeURIComponent(s)}`);
      setItemResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compare failed");
    } finally { setItemLoading(false); }
  };

  const startFull = async () => {
    try {
      const { data } = await api.post("/filemaker/audit/full");
      if (data.started) { toast.success("Full audit started"); setFull({ running: true, phase: "Starting..." }); }
      else toast.message(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
  };

  return (
    <div data-testid="fm-audit-panel" className="border-t border-gray-100 pt-5 space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-label text-gray-500">FileMaker Data Audit</p>
        <p className="text-xs text-gray-500 mt-1">Read-only comparison of app price rows against FileMaker by FM recordId. Nothing is deleted.</p>
      </div>

      <div className="space-y-3" data-testid="fm-audit-item-section">
        <div className="flex items-center gap-2">
          <Input value={itemNo} onChange={(e) => setItemNo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && compareItem()} placeholder="Item No, e.g. K24-WN022" className="h-8 w-56 font-mono text-xs" data-testid="fm-audit-item-input" />
          <Button size="sm" variant="outline" onClick={compareItem} disabled={itemLoading} className="h-8" data-testid="fm-audit-item-button">
            {itemLoading ? <Loader2 size={13} className="animate-spin mr-1" /> : <Search size={13} className="mr-1" />} Compare
          </Button>
        </div>
        {itemResult && (
          <div className="space-y-3" data-testid="fm-audit-item-result">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Stat label="FileMaker rows" value={itemResult.fm_count} />
              <Stat label="App rows" value={itemResult.app_count} />
              <Stat label="Matched" value={itemResult.matched} />
              <Stat label="App only (stale)" value={itemResult.app_only.length} accent={itemResult.app_only.length ? "text-red-600" : "text-gray-900"} />
              <Stat label="FM only (missing)" value={itemResult.fm_only.length} accent={itemResult.fm_only.length ? "text-amber-600" : "text-gray-900"} />
            </div>
            {itemResult.web_created > 0 && <p className="text-xs text-gray-500">{itemResult.web_created} row(s) created in the app (no FM record) are excluded.</p>}
            <p className="text-xs font-semibold text-gray-700">App only — exist in app but not in FileMaker</p>
            <RowsTable rows={itemResult.app_only} testId="fm-audit-app-only-table" />
            <p className="text-xs font-semibold text-gray-700">FileMaker only — not yet synced into app</p>
            <RowsTable rows={itemResult.fm_only} testId="fm-audit-fm-only-table" />
          </div>
        )}
      </div>

      <div className="space-y-3 border-t border-gray-100 pt-4" data-testid="fm-audit-full-section">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-700">Full database audit</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={loadStatus} className="h-8" data-testid="fm-audit-full-refresh"><RefreshCw size={13} /></Button>
            <Button size="sm" onClick={startFull} disabled={full.running} className="h-8" data-testid="fm-audit-full-start">
              {full.running ? <Loader2 size={13} className="animate-spin mr-1" /> : null}{full.running ? full.phase || "Running..." : "Run full audit"}
            </Button>
          </div>
        </div>
        {full.error && <p className="text-xs text-red-600" data-testid="fm-audit-full-error">{full.error}</p>}
        {full.finished_at && (
          <div className="space-y-3" data-testid="fm-audit-full-result">
            <p className="text-xs text-gray-400">Last run: {new Date(full.finished_at).toLocaleString()}</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Stat label="FileMaker rows" value={full.fm_total} />
              <Stat label="App rows" value={full.app_total} />
              <Stat label="Matched" value={full.matched} />
              <Stat label="App only (stale)" value={full.app_only_count} accent={full.app_only_count ? "text-red-600" : "text-gray-900"} />
              <Stat label="FM only (not synced)" value={full.fm_only_count} accent={full.fm_only_count ? "text-amber-600" : "text-gray-900"} />
            </div>
            {full.app_only_by_item?.length > 0 && (
              <div className="overflow-x-auto border border-gray-100 rounded-md max-h-72 overflow-y-auto">
                <table className="w-full" data-testid="fm-audit-full-table">
                  <thead className="bg-gray-50 sticky top-0"><tr><Th>Item No</Th><Th>Stale rows</Th><Th>Contracts</Th></tr></thead>
                  <tbody>
                    {full.app_only_by_item.map((g) => (
                      <tr key={g.item_no} className="border-t border-gray-100">
                        <Td mono>{g.item_no}</Td><Td>{g.count}</Td><Td mono>{g.contracts.join(", ")}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
